import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;
  #isKeyboardNavigation = false;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** CLICK HANDLER */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const isInteractive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button, a, input');

    if (!isInteractive && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /** SHORTCUT CMD+K */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    return containers
      .flatMap((container) =>
        container.classList.contains('predictive-search-results__wrapper-products')
          ? Array.from(container.querySelectorAll('.predictive-search-results__card'))
          : Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'))
      )
      .filter((el) => el instanceof HTMLElement);
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((el) => el.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items.length) return;

    items.forEach((item) => item.classList.remove('keyboard-focus'));

    items.forEach((item, i) => {
      if (i === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /** KEYBOARD NAVIGATION */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // if (event.key === 'Enter' || event.key === 'NumpadEnter') {
    //   event.preventDefault();
    //   const query = this.refs.searchInput.value.trim();

    //   if (!query) return this.dialog?.closeDialog();

    //   setTimeout(() => {
    //     const article = document.querySelector('a[href*="/blogs/news/"]');
    //     if (article?.href) return (window.location.href = article.href);

    //     const highlighted = document.querySelector('[aria-selected="true"] a');
    //     if (highlighted?.href) return (window.location.href = highlighted.href);

    //     window.location.href = '/pages/list-articles';
    //   }, 200);

    //   return;
    // }

    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      event.preventDefault();
      const query = this.refs.searchInput.value.trim();

      if (!query) {
        this.dialog?.closeDialog();
        return;
      }

      // 🔥 LANGSUNG redirect ke halaman search custom
      const encoded = encodeURIComponent(query);

      window.location.href = `/pages/list-articles?q=${encoded}`;
      return;
    }

    if (!this.#allResultsItems?.length) return;

    const currentIndex = this.#currentIndex;
    const total = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = currentIndex < total - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : total - 1;
        break;

      case 'Tab':
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        if (event.shiftKey) {
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : total - 1;
        } else {
          this.#currentIndex = currentIndex < total - 1 ? currentIndex + 1 : 0;
        }
        break;
    }
  };

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(term);
  }, 200);

  /** PRIVATE UTILITIES ------------------------ */

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const controller = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = controller;
    return controller;
  }

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (inner) inner.scrollTop = 0;

      const content = this.querySelector('.predictive-search-form__content');
      if (content) content.scrollTop = 0;
    });
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewed = RecentlyViewed.getProducts();
    if (!viewed.length) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewed.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const locale = Shopify.locale;
    console.log(`/${locale}/search/suggest`)
    const url = new URL(`/${locale}/search/suggest`, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,article,collection,page');
    url.searchParams.set('resources[limit_scope]', 'each');

    const localeRoot = window.Shopify?.routes?.root || '/';
    url.searchParams.set('section_url', localeRoot + 'search');

    const abortController = this.#createAbortController();

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!html || abortController.signal.aborted) return;

      morph(this.refs.predictiveSearchResults, html);
      this.#resetScrollPositions();
    } catch (err) {
      if (!abortController.signal.aborted) console.error(err);
    }
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptyMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsed = new DOMParser()
      .parseFromString(emptyMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsed) throw new Error('No empty section markup found');

    // const viewed = RecentlyViewed.getProducts();
    // if (viewed.length > 0) {
    //   const rvMarkup = await this.#getRecentlyViewedProductsMarkup();
    //   if (rvMarkup) {
    //     const parsedRV = new DOMParser().parseFromString(rvMarkup, 'text/html');
    //     const rvHtml = parsedRV.getElementById('predictive-search-products');
    //     const target = parsed.querySelector('#predictive-search-products');
    //     if (rvHtml && target) target.prepend(...rvHtml.children);
    //   }
    // }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsed);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
